import React, {useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {doc, getDoc, serverTimestamp, updateDoc} from 'firebase/firestore'
import {useAuth} from '../../../../modules/auth'
import db from '../../../../../services/firebase'
import Button from 'app/components/Button'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import FormatTimestamp from 'app/components/FormatTimestamp'

interface StartReviewProps {
  isOpen: boolean
  onClose: () => void
  id: string
  dueDate: string
  reviewer: {
    id: string
    name: string
    email: string
  }
}

const StartReviewModal: React.FC<StartReviewProps> = ({isOpen, onClose, id, dueDate, reviewer}) => {
  const [showModal, setShowModal] = useState(false)
  const {currentUser} = useAuth()
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setShowModal(isOpen)
  }, [isOpen])

  const handleClose = () => {
    setShowModal(false)
    onClose()
  }

  const handleStartReview = async () => {
    try {
      setIsLoading(true)

      const tenantId = currentUser?.tenantId || ''
      const testDocRef = doc(db, `tenants/${tenantId}/reviews/${id}`)

      const reviewSnapshot = await getDoc(testDocRef)

      if (!reviewSnapshot.exists()) {
        throw new Error('Review document does not exist')
      }

      const reviewData = reviewSnapshot.data()

      if (!reviewData.systems) {
        throw new Error('No systems found in review document')
      }

      const updatedSystems = {...reviewData.systems}
      Object.keys(updatedSystems).forEach((systemId) => {
        updatedSystems[systemId] = {
          ...updatedSystems[systemId],
          reviewer: reviewer,
          status: 'In review',
        }
      })

      await updateDoc(testDocRef, {
        status: 'In review',
        startDate: serverTimestamp(),
        systems: updatedSystems,
      })

      handleClose()
      setIsLoading(false)
      showSuccessToast({message: 'Review started successfully!'})
    } catch (error) {
      console.error(error)
      showErrorToast({message: 'Failed to start review'})
      setIsLoading(false)
    }
  }

  return (
    <Modal show={showModal} onHide={handleClose} backdropClassName='bg-opacity-1' centered>
      <Modal.Header style={{padding: '20px'}}>
        <Modal.Title>Start the review</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{padding: '20px'}}>
        <div className='fs-5 fw-bold mb-3'>Get ready to start your review </div>
        <div className='fs-6' style={{color: '#5A5A5A'}}>
          Starting the access review will notify all designated reviewers. Please note that once the
          review has been initiated, modifications to systems or re-uploading of access files will
          no longer be permitted. <br />
          <br />
          Kindly ensure the review is completed by{' '}
          <strong>{FormatTimestamp({timestamp: dueDate})}</strong>.
        </div>
      </Modal.Body>
      <Modal.Footer className='p-0 px-6 py-4 gap-2'>
        <Button onClick={handleClose}>Cancel</Button>
        <Button btnType='primary' onClick={handleStartReview}>
          {isLoading ? (
            <>
              Starting... <span className='spinner-border spinner-border-sm align-middle'></span>
            </>
          ) : (
            'Start Review'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default StartReviewModal
